import THREE from '@/services/three/three-core'
import { createCanvasAdapter } from './avatarEngine/canvasAdapter'
import { stripMouthTriangles } from './avatarEngine/faceMath'
import { FACE_MESH_CONTOUR_PATHS } from './avatarInspector/featureIndices'

class AvatarGeometryRenderer {
  constructor({ width = 640, height = 360, onDebug = null } = {}) {
    this._width = width
    this._height = height
    this._onDebug = typeof onDebug === 'function' ? onDebug : null

    this._canvasAdapter = createCanvasAdapter()

    this._canvas = null
    this._renderer = null
    this._scene = null
    this._camera = null
    this._facePositionAttr = null
    this._hairBackPositionAttr = null
    this._hairFrontPositionAttr = null

    this._faceMesh = null
    this._hairBackMesh = null
    this._hairFrontMesh = null
    this._textureCache = new Map()
    this._geometries = []
    this._faceMaterial = null
    this._hairBackMaterial = null
    this._hairFrontMaterial = null

    this._pointCount = 0
    this._srcPoints = null
    this._srcWidth = 1
    this._srcHeight = 1
    this._hairMeshPointCount = 0
    this._hairMeshPoints = null
    this._hairMeshDepth = null
    this._hairUsesCustomPoints = false
    this._useBitmapUV = false
    this._assetKey = null
    this._runtime = null
    this._resourceManager = null
    this._disposed = false
    this._meshOutDst = null
    this._meshOutDepth = null
    this._meshOutCount = 0
    this._contextLost = false
    this._lastContextLostAt = 0
    this._ctxLossBound = false
  }

  _debug(message) {
    if (this._onDebug) this._onDebug(`[GeometryRenderer] ${message}`)
  }

  _ensureRenderer() {
    if (this._renderer) return true
    if (this._contextLost) {
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
      if (now - this._lastContextLostAt < 2000) return false
      this._contextLost = false
    }
    if (typeof document === 'undefined' && typeof OffscreenCanvas === 'undefined') return false

    try {
      this._canvas = this._canvasAdapter.createCanvas(this._width, this._height)
      this._canvas.width = this._width
      this._canvas.height = this._height

      this._renderer = new THREE.WebGLRenderer({
        canvas: this._canvas,
        alpha: true,
        antialias: true,
        premultipliedAlpha: true,
        preserveDrawingBuffer: false
      })
      const deviceRatio =
        typeof globalThis !== 'undefined' && typeof globalThis.devicePixelRatio === 'number'
          ? globalThis.devicePixelRatio
          : 1
      this._renderer.setPixelRatio(Math.min(deviceRatio || 1, 1))
      this._renderer.setSize(this._width, this._height, false)
      this._renderer.autoClear = true
      this._renderer.outputColorSpace = THREE.SRGBColorSpace
      this._bindContextLossHandlers()

      this._scene = new THREE.Scene()
      this._camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 10)
      this._camera.position.set(0, 0, 1)
      this._camera.lookAt(0, 0, 0)
      // register canvas with resource manager if available
      try {
        if (this._resourceManager && typeof this._resourceManager.registerCanvas === 'function') {
          this._resourceManager.registerCanvas(this._canvas)
          if (typeof this._resourceManager.registerDisposable === 'function') {
            this._resourceManager.registerDisposable(() => {
              try {
                this.dispose()
              } catch (e) {}
            })
          }
        }
      } catch (e) {}
      return true
    } catch (err) {
      this._debug(`init failed: ${err?.message || 'unknown error'}`)
      this._markContextLost()
      return false
    }
  }

  _bindContextLossHandlers() {
    if (this._ctxLossBound || !this._canvas) return
    if (typeof this._canvas.addEventListener !== 'function') return
    this._ctxLossBound = true
    this._canvas.addEventListener('webglcontextlost', (e) => {
      try {
        if (e && typeof e.preventDefault === 'function') e.preventDefault()
      } catch {}
      this._markContextLost()
    })
    this._canvas.addEventListener('webglcontextrestored', () => {
      this._contextLost = false
      this._lastContextLostAt = 0
      this._resetRenderer()
    })
  }

  _markContextLost() {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    this._contextLost = true
    this._lastContextLostAt = now
    this._resetRenderer()
  }

  _resetRenderer() {
    this._disposeMeshes()
    this._textureCache.forEach((entry) => {
      try {
        entry?.texture?.dispose?.()
      } catch (e) {}
    })
    this._textureCache.clear()
    try {
      this._faceMaterial?.dispose?.()
    } catch (e) {}
    try {
      this._hairBackMaterial?.dispose?.()
    } catch (e) {}
    try {
      this._hairFrontMaterial?.dispose?.()
    } catch (e) {}
    this._faceMaterial = null
    this._hairBackMaterial = null
    this._hairFrontMaterial = null
    try {
      if (this._renderer) {
        this._renderer.dispose()
        try {
          this._renderer.forceContextLoss()
        } catch (e) {}
      }
    } catch (e) {}
    this._renderer = null
    this._scene = null
    this._camera = null
    this._assetKey = null
  }

  setSize(width, height) {
    if (!Number.isFinite(width) || !Number.isFinite(height)) return
    if (width <= 0 || height <= 0) return
    this._width = Math.round(width)
    this._height = Math.round(height)
    if (this._renderer) {
      this._canvas.width = this._width
      this._canvas.height = this._height
      const deviceRatio =
        typeof globalThis !== 'undefined' && typeof globalThis.devicePixelRatio === 'number'
          ? globalThis.devicePixelRatio
          : 1
      this._renderer.setPixelRatio(Math.min(deviceRatio || 1, 1))
      this._renderer.setSize(this._width, this._height, false)
    }
  }

  _disposeMeshes() {
    if (this._faceMesh) {
      this._scene?.remove(this._faceMesh)
      this._faceMesh = null
    }
    if (this._hairBackMesh) {
      this._scene?.remove(this._hairBackMesh)
      this._hairBackMesh = null
    }
    if (this._hairFrontMesh) {
      this._scene?.remove(this._hairFrontMesh)
      this._hairFrontMesh = null
    }
    this._geometries.forEach((g) => g.dispose())
    this._geometries = []
    this._facePositionAttr = null
    this._hairBackPositionAttr = null
    this._hairFrontPositionAttr = null
    this._hairMeshPointCount = 0
    this._hairMeshPoints = null
    this._hairMeshDepth = null
    this._hairUsesCustomPoints = false
  }

  _createTexture(sourceCanvas) {
    if (!sourceCanvas) return null
    // Downscale overly large canvases to avoid GPU memory spikes when zooming/resizing
    const MAX_TEX = 2048
    const isCanvas = !!(sourceCanvas && typeof sourceCanvas.getContext === 'function')
    if (!isCanvas) {
      const cached = this._textureCache.get(sourceCanvas)
      if (cached) return cached.texture
      const texture = new THREE.Texture(sourceCanvas)
      texture.premultiplyAlpha = true
      texture.colorSpace = THREE.SRGBColorSpace
      texture.minFilter = THREE.LinearFilter
      texture.magFilter = THREE.LinearFilter
      texture.generateMipmaps = false
      texture.needsUpdate = true
      this._textureCache.set(sourceCanvas, {
        texture,
        width: sourceCanvas?.width || 0,
        height: sourceCanvas?.height || 0,
        isCanvas: false
      })
      return texture
    }

    const srcW = Math.max(1, sourceCanvas.width || 1)
    const srcH = Math.max(1, sourceCanvas.height || 1)
    const scale = Math.min(1, MAX_TEX / srcW, MAX_TEX / srcH)
    const targetW = Math.max(1, Math.round(srcW * scale))
    const targetH = Math.max(1, Math.round(srcH * scale))

    const cached = this._textureCache.get(sourceCanvas)
    if (cached && cached.isCanvas && cached.width === targetW && cached.height === targetH) {
      if (cached.ctx && cached.canvas) {
        try {
          cached.ctx.clearRect(0, 0, targetW, targetH)
          cached.ctx.drawImage(sourceCanvas, 0, 0, targetW, targetH)
        } catch (e) {}
      }
      cached.texture.needsUpdate = true
      return cached.texture
    }

    if (cached && cached.texture) {
      try {
        cached.texture.dispose()
      } catch (e) {}
    }

    let stable = null
    let ctx = null
    try {
      stable = this._canvasAdapter.createCanvas(targetW, targetH)
      stable.width = targetW
      stable.height = targetH
      ctx = this._canvasAdapter.getContext(stable, '2d', { willReadFrequently: true })
      ctx.drawImage(sourceCanvas, 0, 0, targetW, targetH)
      if (this._resourceManager && typeof this._resourceManager.registerCanvas === 'function') {
        this._resourceManager.registerCanvas(stable)
        if (typeof this._resourceManager.registerDisposable === 'function') {
          this._resourceManager.registerDisposable(() => {
            try {
              stable.width = 0
              stable.height = 0
            } catch (e) {}
          })
        }
      }
    } catch (e) {
      stable = sourceCanvas
      ctx = null
    }

    const texture = new THREE.CanvasTexture(stable)
    texture.premultiplyAlpha = true
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.generateMipmaps = false
    texture.needsUpdate = true
    this._textureCache.set(sourceCanvas, {
      texture,
      width: targetW,
      height: targetH,
      isCanvas: true,
      canvas: stable,
      ctx
    })
    return texture
  }

  _createGeometry({ pointCount, triangles, srcPoints, srcWidth, srcHeight, positionAttr = null }) {
    const geometry = new THREE.BufferGeometry()

    let posAttr = positionAttr
    if (!posAttr || posAttr.count !== pointCount) {
      posAttr = new THREE.BufferAttribute(new Float32Array(pointCount * 3), 3)
      posAttr.setUsage(THREE.DynamicDrawUsage)
    }
    geometry.setAttribute('position', posAttr)

    const uv = new Float32Array(pointCount * 2)
    const useBitmapUV = this._useBitmapUV === true
    for (let i = 0; i < pointCount; i += 1) {
      const p = srcPoints[i]
      const u = p ? p.x / Math.max(1, srcWidth) : 0
      const v = p
        ? useBitmapUV
          ? p.y / Math.max(1, srcHeight)
          : 1 - p.y / Math.max(1, srcHeight)
        : 0
      uv[i * 2] = u
      uv[i * 2 + 1] = v
    }
    geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
    geometry.setIndex(triangles)
    this._geometries.push(geometry)
    return { geometry, positionAttr: posAttr }
  }

  setAvatarLayers({ avatarRig, avatarLayers, assetKey = null }) {
    if (!avatarRig?.pointsPx?.length || !avatarRig?.meshCount) return false
    if (!avatarLayers?.faceCanvas) return false
    if (!this._ensureRenderer()) return false

    const faceSource = avatarLayers.faceCanvas
    this._useBitmapUV =
      !!faceSource &&
      typeof ImageBitmap !== 'undefined' &&
      typeof faceSource.getContext !== 'function' &&
      faceSource instanceof ImageBitmap

    const pointCount = avatarRig.meshCount
    const srcPoints = avatarRig.pointsPx
    const srcWidth = avatarRig.imageWidth
    const srcHeight = avatarRig.imageHeight
    const baseFaceTriangles = avatarLayers.faceTriangles || avatarRig.triangles || []
    const faceTriangles = stripMouthTriangles(baseFaceTriangles, FACE_MESH_CONTOUR_PATHS.lipsInner)
    // For hair: do NOT fall back to full face triangles. If extraction failed
    // we prefer rendering no hair meshes rather than incorrectly drawing the face.
    const hairTriangles =
      Array.isArray(avatarLayers.hairTriangles) && avatarLayers.hairTriangles.length
        ? avatarLayers.hairTriangles
        : []
    const hairFrontTriangles = hairTriangles

    const nextKey =
      assetKey ||
      `${srcWidth}x${srcHeight}:${pointCount}:${faceTriangles.length}:${hairTriangles.length}`
    if (this._assetKey === nextKey && this._faceMesh) {
      return true
    }
    this._assetKey = nextKey

    this._disposeMeshes()

    this._pointCount = pointCount
    this._srcPoints = srcPoints
    this._srcWidth = srcWidth
    this._srcHeight = srcHeight

    const faceTexture = this._createTexture(avatarLayers.faceCanvas)
    const faceGeometryRes = this._createGeometry({
      pointCount,
      triangles: faceTriangles,
      srcPoints,
      srcWidth,
      srcHeight,
      positionAttr: this._facePositionAttr
    })
    this._facePositionAttr = faceGeometryRes.positionAttr
    if (!this._faceMaterial) {
      this._faceMaterial = new THREE.MeshBasicMaterial({
        map: faceTexture,
        transparent: true,
        alphaTest: 0.15,
        premultipliedAlpha: true,
        depthTest: true,
        depthWrite: true,
        side: THREE.DoubleSide
      })
    } else {
      this._faceMaterial.map = faceTexture
      this._faceMaterial.needsUpdate = true
    }
    this._faceMesh = new THREE.Mesh(faceGeometryRes.geometry, this._faceMaterial)
    this._faceMesh.renderOrder = 1
    this._scene.add(this._faceMesh)

    const hairOpaque = Number(avatarLayers?.hairOpaque || 0)
    const minHairOpaque = 16
    const hairMesh = avatarLayers?.hairMesh || null
    const hasHairMesh =
      hairMesh?.points?.length && Array.isArray(hairMesh?.triangles) && hairMesh.triangles.length
    const hairPoints = hasHairMesh ? hairMesh.points : srcPoints
    const hairPointCount = hasHairMesh ? hairMesh.points.length : pointCount
    this._hairUsesCustomPoints = !!hasHairMesh
    this._hairMeshPointCount = hasHairMesh ? hairPointCount : 0
    this._hairMeshPoints = hasHairMesh ? hairPoints : null
    this._hairMeshDepth =
      hasHairMesh && hairMesh?.depth && hairMesh.depth.length === hairPointCount
        ? hairMesh.depth
        : null
    const hairFrontCanvas = hairOpaque > minHairOpaque ? avatarLayers.hairCanvas : null
    const hairFrontTriSource = hasHairMesh ? hairMesh.triangles : hairFrontTriangles
    if (hairFrontCanvas && hairFrontTriSource.length) {
      const hairFrontTexture = this._createTexture(hairFrontCanvas)
      const hairFrontGeomRes = this._createGeometry({
        pointCount: hairPointCount,
        triangles: hairFrontTriSource,
        srcPoints: hairPoints,
        srcWidth,
        srcHeight,
        positionAttr: hasHairMesh ? this._hairFrontPositionAttr : this._facePositionAttr
      })
      this._hairFrontPositionAttr = hasHairMesh
        ? hairFrontGeomRes.positionAttr
        : this._facePositionAttr
      if (!this._hairFrontMaterial) {
        this._hairFrontMaterial = new THREE.MeshBasicMaterial({
          map: hairFrontTexture,
          transparent: true,
          alphaTest: 0.15,
          premultipliedAlpha: true,
          depthTest: true,
          depthWrite: true,
          side: THREE.DoubleSide
        })
      } else {
        this._hairFrontMaterial.map = hairFrontTexture
        this._hairFrontMaterial.needsUpdate = true
      }
      this._hairFrontMesh = new THREE.Mesh(hairFrontGeomRes.geometry, this._hairFrontMaterial)
      this._hairFrontMesh.renderOrder = 2
      this._hairFrontMesh.position.z = 0.02
      this._scene.add(this._hairFrontMesh)
    }

    return true
  }

  setRuntime(runtime = null) {
    this._runtime = runtime || null
  }

  setResourceManager(resourceManager = null) {
    this._resourceManager = resourceManager || null
  }

  setMeshOutputCallback(cb = null) {
    this._meshOutputCb = typeof cb === 'function' ? cb : null
  }

  clearAvatarLayers() {
    if (!this._renderer) return
    this._disposeMeshes()
    this._assetKey = null
    this._pointCount = 0
    this._srcPoints = null
    this._hairMeshPointCount = 0
    this._hairMeshPoints = null
    this._hairMeshDepth = null
    this._hairUsesCustomPoints = false
    this._meshOutDst = null
    this._meshOutDepth = null
    this._meshOutCount = 0
  }

  renderToContext(
    ctx,
    {
      dstPoints,
      dstDepth = null,
      hairDstPoints = null,
      hairDstDepth = null,
      drawFace = true,
      drawHair = true,
      depthStrength = 0.12,
      faceDepthOffset = 0,
      hairFrontDepthOffset = 0.02
    } = {}
  ) {
    if (this._contextLost) return false
    if (!ctx || !dstPoints?.length) return false
    if (!this._renderer || !this._faceMesh || !this._facePositionAttr) return false
    if (dstPoints.length < this._pointCount) return false

    const positions = this._facePositionAttr.array
    const runtime = this._runtime || {}
    const depthMultiplier = Number.isFinite(runtime.faceGeometryBlend)
      ? runtime.faceGeometryBlend
      : 1
    const finalDepthStrength = (Number(depthStrength) || 0) * depthMultiplier
    for (let i = 0; i < this._pointCount; i += 1) {
      const p = dstPoints[i]
      const ndcX = (p.x / Math.max(1, this._width)) * 2 - 1
      const ndcY = 1 - (p.y / Math.max(1, this._height)) * 2
      const pi = i * 3
      positions[pi] = ndcX
      positions[pi + 1] = ndcY
      const depth = dstDepth?.[i] ?? 0
      positions[pi + 2] = depth * finalDepthStrength
    }
    this._facePositionAttr.needsUpdate = true

    const updateHairPositions = (attr, pts, depth) => {
      if (!attr || !pts?.length) return
      const arr = attr.array
      const count = Math.min(attr.count, pts.length)
      for (let i = 0; i < count; i += 1) {
        const p = pts[i]
        const ndcX = (p.x / Math.max(1, this._width)) * 2 - 1
        const ndcY = 1 - (p.y / Math.max(1, this._height)) * 2
        const pi = i * 3
        arr[pi] = ndcX
        arr[pi + 1] = ndcY
        const d = depth?.[i] ?? 0
        arr[pi + 2] = d * finalDepthStrength
      }
      attr.needsUpdate = true
    }

    if (this._hairUsesCustomPoints) {
      const hairPts = hairDstPoints || dstPoints
      const hairDepth = hairDstDepth || this._hairMeshDepth || dstDepth
      if (this._hairFrontPositionAttr && this._hairFrontPositionAttr !== this._facePositionAttr) {
        updateHairPositions(this._hairFrontPositionAttr, hairPts, hairDepth)
      }
    }

    if (this._faceMesh) {
      this._faceMesh.visible = drawFace
      const jawOpen = (runtime.facialSignals && runtime.facialSignals.jawOpen) || 0
      this._faceMesh.position.z = faceDepthOffset + jawOpen * 0.02
    }
    if (this._hairFrontMesh) {
      this._hairFrontMesh.visible = drawHair
      this._hairFrontMesh.position.z = hairFrontDepthOffset
    }

    try {
      this._renderer.setClearColor(0x000000, 0)
      this._renderer.clear(true, true, true)
      this._renderer.render(this._scene, this._camera)
    } catch (e) {
      this._debug(`render failed: ${e?.message || 'unknown error'}`)
      this._markContextLost()
      return false
    }

    // publish rendered canvas to 2D context
    try {
      ctx.drawImage(this._canvas, 0, 0, this._width, this._height)
    } catch (e) {
      this._debug(`drawImage failed: ${e?.message || 'unknown error'}`)
      return false
    }

    // if a mesh output callback is provided, compute dst points and depth from position attribute
    try {
      if (this._meshOutputCb && this._facePositionAttr && this._facePositionAttr.array) {
        const arr = this._facePositionAttr.array
        const count = this._pointCount
        let dst = this._meshOutDst
        let depthBuf = this._meshOutDepth
        if (!dst || this._meshOutCount !== count || !Array.isArray(dst)) {
          dst = new Array(count)
          for (let i = 0; i < count; i += 1) {
            dst[i] = { x: 0, y: 0 }
          }
          this._meshOutDst = dst
          this._meshOutCount = count
        }
        if (!depthBuf || depthBuf.length !== count) {
          depthBuf = new Float32Array(count)
          this._meshOutDepth = depthBuf
        }
        const depthMult = finalDepthStrength || 1
        for (let i = 0; i < count; i += 1) {
          const pi = i * 3
          const ndcX = arr[pi]
          const ndcY = arr[pi + 1]
          const z = arr[pi + 2]
          const x = (ndcX + 1) * 0.5 * this._width
          const y = (1 - ndcY) * 0.5 * this._height
          const p = dst[i]
          if (p) {
            p.x = x
            p.y = y
          } else {
            dst[i] = { x, y }
          }
          depthBuf[i] = Number.isFinite(depthMult) && depthMult !== 0 ? z / depthMult : z
        }
        try {
          // publish both legacy and normalized keys so callers can consume either
          this._meshOutputCb({
            warpDstPoints: dst,
            warpDepthBuffer: depthBuf,
            dstPoints: dst,
            dstDepth: depthBuf
          })
        } catch {}
      }
    } catch {}

    return true
  }

  dispose() {
    if (this._disposed) return
    this._disposed = true
    this._resetRenderer()
    // clear large typed arrays to aid GC
    try {
      if (this._facePositionAttr && this._facePositionAttr.array)
        this._facePositionAttr.array = null
      if (this._hairBackPositionAttr && this._hairBackPositionAttr.array)
        this._hairBackPositionAttr.array = null
      if (this._hairFrontPositionAttr && this._hairFrontPositionAttr.array)
        this._hairFrontPositionAttr.array = null
    } catch (e) {}
    this._facePositionAttr = null
    this._hairBackPositionAttr = null
    this._hairFrontPositionAttr = null
    this._canvas = null
    this._meshOutputCb = null
    this._assetKey = null
    this._pointCount = 0
    this._srcPoints = null
    this._meshOutDst = null
    this._meshOutDepth = null
    this._meshOutCount = 0
  }
}

export default AvatarGeometryRenderer
